import type { HTMLAttributeAnchorTarget } from 'react'

import { PERMISSIONS } from '../constants'

import { openChat } from 'lib/zendesk'

type GroupItem = {
  label: string
  href?: string
  id: string
  target?: HTMLAttributeAnchorTarget
  action?: () => void
  permission: PERMISSIONS[]
}

type Group = {
  group_name: string
  group_permissions: PERMISSIONS[]
  href?: string
  target_new?: boolean
  icon: string
  group_list?: GroupItem[]
  action?: () => void
}

type MenuItem = {
  session_name: string
  session_permissions: PERMISSIONS[]
  session_groups: Group[]
}

type MenuList = MenuItem | null

export const mountMenuList = ({
  swileCreditEnabled = false,
  hasPartnersEnabled = false,
  isRecardingEnabled = false,
  recardingPendingCount = 0
}: {
  swileCreditEnabled: boolean
  hasPartnersEnabled: boolean
  isRecardingEnabled: boolean
  recardingPendingCount: number
}): MenuItem[] => {
  const menu: MenuList[] = [
    addItemWhenHasFeatureAllowed<MenuItem>(
      {
        session_name: 'Financeiro',
        session_permissions: [
          PERMISSIONS.VIEW_ORDERS,
          PERMISSIONS.MANAGE_ORDERS,
          PERMISSIONS.MANAGE_SWILE_CREDIT,
          PERMISSIONS.CREATE_SWILE_CREDIT
        ],
        session_groups: prepareGroups([
          addItemWhenHasFeatureAllowed(
            {
              group_name: 'Pedidos',
              group_permissions: [
                PERMISSIONS.VIEW_ORDERS,
                PERMISSIONS.MANAGE_ORDERS
              ],
              icon: 'wallet',
              href: '/orders',
              id: 'link-orders'
            },
            'FINANCE_ORDERS'
          ),
          swileCreditEnabled
            ? addItemWhenHasFeatureAllowed(
                {
                  group_name: 'Swile crédito',
                  icon: 'cashback-alt',
                  group_permissions: [
                    PERMISSIONS.MANAGE_SWILE_CREDIT,
                    PERMISSIONS.CREATE_SWILE_CREDIT
                  ],
                  href: '/swile-credit',
                  id: 'link-swile-credit'
                },
                'FINANCE_CREDIT'
              )
            : null
        ]) as Group[]
      },
      'FINANCE'
    ),
    addItemWhenHasFeatureAllowed<MenuItem>(
      {
        session_name: 'Cartões',
        session_permissions: [PERMISSIONS.VIEW_CARDS, PERMISSIONS.MANAGE_CARDS],
        session_groups: prepareGroups([
          isRecardingEnabled
            ? addItemWhenHasFeatureAllowed(
                {
                  group_name: `Troca de cartões ${
                    recardingPendingCount > 0
                      ? `(${recardingPendingCount})`
                      : ''
                  }`,
                  group_permissions: [
                    PERMISSIONS.MANAGE_CARDS,
                    PERMISSIONS.VIEW_CARDS
                  ],
                  icon: 'cards',
                  href: '/recarding',
                  id: 'link-cards-replacement'
                },
                'RECARDING'
              )
            : null,
          addItemWhenHasFeatureAllowed(
            {
              group_name: 'Solicitação de cartões',
              group_permissions: [
                PERMISSIONS.VIEW_CARDS,
                PERMISSIONS.MANAGE_CARDS
              ],
              icon: 'truck',
              href: '/cards',
              id: 'link-cards'
            },
            'CARDS_REQUEST'
          )
        ]) as Group[]
      },
      'CARDS'
    ),
    addItemWhenHasFeatureAllowed<MenuItem>(
      {
        session_name: 'Pessoas',
        session_permissions: [
          PERMISSIONS.VIEW_WORKGROUPS,
          PERMISSIONS.MANAGE_WORKGROUPS,
          PERMISSIONS.MANAGE_EMPLOYEES,
          PERMISSIONS.VIEW_EMPLOYEES,
          PERMISSIONS.MANAGE_CARDS
        ],
        session_groups: prepareGroups([
          addItemWhenHasFeatureAllowed(
            {
              group_name: 'Grupos',
              group_permissions: [
                PERMISSIONS.MANAGE_WORKGROUPS,
                PERMISSIONS.VIEW_WORKGROUPS
              ],
              icon: 'group',
              href: '/groups',
              id: 'link-groups'
            },
            'PEOPLE_WORKGORUPS'
          ),
          addItemWhenHasFeatureAllowed(
            {
              group_name: 'Colaboradores',
              group_permissions: [
                PERMISSIONS.MANAGE_EMPLOYEES,
                PERMISSIONS.VIEW_EMPLOYEES
              ],
              icon: 'user',
              href: '/employees',
              id: 'link-employees'
            },
            'PEOPLE_EMPLOYEES'
          )
        ]) as Group[]
      },
      'PEOPLE'
    ),
    addItemWhenHasFeatureAllowed<MenuItem>(
      {
        session_name: 'Empresa',
        session_permissions: [
          PERMISSIONS.MANAGE_CORPORATE,
          PERMISSIONS.VIEW_BRANCHES,
          PERMISSIONS.MANAGE_BRANCHES,
          PERMISSIONS.MANAGE_USERS,
          PERMISSIONS.MANAGE_ROLES,
          PERMISSIONS.VIEW_USER_ACTIVITIES
        ],
        session_groups: prepareGroups([
          {
            group_name: 'Matriz',
            group_permissions: [PERMISSIONS.MANAGE_CORPORATE],
            icon: 'building',
            href: '/corporate',
            id: 'link-corporate'
          },
          {
            group_name: 'Filiais',
            group_permissions: [
              PERMISSIONS.MANAGE_BRANCHES,
              PERMISSIONS.VIEW_BRANCHES
            ],
            icon: 'building-settings',
            href: '/branches',
            id: 'link-branches'
          },
          addItemWhenHasFeatureAllowed(
            {
              group_name: 'Logins de acesso',
              group_permissions: [
                PERMISSIONS.MANAGE_USERS,
                PERMISSIONS.MANAGE_ROLES,
                PERMISSIONS.VIEW_USER_ACTIVITIES
              ],
              icon: 'shield',
              group_list: prepareGroups([
                addItemWhenHasFeatureAllowed(
                  {
                    label: 'Usuários',
                    href: '/users',
                    id: 'link-users',
                    permission: [PERMISSIONS.MANAGE_USERS]
                  },
                  'CORPORATE_LOGIN_USERS'
                ),
                addItemWhenHasFeatureAllowed(
                  {
                    label: 'Tipos de acesso',
                    href: '/roles',
                    id: 'link-roles',
                    permission: [PERMISSIONS.MANAGE_ROLES]
                  },
                  'CORPORATE_LOGIN_ROLES'
                ),
                addItemWhenHasFeatureAllowed(
                  {
                    href: '/action-history',
                    label: 'Histórico de ações',
                    id: 'link-action-history',
                    permission: [PERMISSIONS.VIEW_USER_ACTIVITIES]
                  },
                  'CORPORATE_LOGIN_HISTORY'
                )
              ])
            },
            'CORPORATE_LOGIN'
          ),
          {
            group_name: 'Configurações',
            group_permissions: [PERMISSIONS.MANAGE_CORPORATE],
            icon: 'gear',
            href: '/configs',
            id: 'link-configs'
          }
        ]) as Group[]
      },
      'CORPORATE'
    ),
    {
      session_name: 'Suporte',
      session_permissions: [],
      session_groups: [
        {
          group_name: 'Dúvidas',
          icon: 'question-mark-in-box',
          href: 'https://ajuda.swile.com.br/hc/pt-br/categories/1500000260482',
          group_permissions: [],
          target_new: true
        },
        {
          group_name: 'Tutoriais',
          icon: 'video',
          href: 'https://materiais.swile.co/pt-br/onboarding-rh',
          group_permissions: [],
          target_new: true
        },
        {
          group_name: 'Fale com a Swile',
          icon: 'chat',
          group_list: [
            {
              label: 'Deixe uma mensagem',
              href: 'https://ajuda.swile.com.br/hc/pt-br/requests/new',
              id: 'link-message',
              target: '_blank',
              permission: []
            },
            {
              label: 'Acesse nosso chat',
              permission: [],
              action: openChat,
              id: 'button-open-chat'
            }
          ],
          group_permissions: []
        }
      ]
    }
  ]

  if (hasPartnersEnabled) {
    menu.unshift(
      addItemWhenHasFeatureAllowed<MenuItem>(
        {
          session_name: 'Serviços adicionais',
          session_permissions: [PERMISSIONS.VIEW_SWILE_MAIS],
          session_groups: prepareGroups([
            addItemWhenHasFeatureAllowed(
              {
                group_name: 'Swile +',
                group_permissions: [PERMISSIONS.VIEW_SWILE_MAIS],
                icon: 'star',
                href: '/swile-mais',
                id: 'link-swile-mais'
              },
              'SERVICES_SWILE_MAIS'
            )
          ]) as Group[]
        },
        'SERVICES'
      )
    )
  }

  return prepareGroups(menu) as MenuItem[]
}

type RouteRules =
  | 'FINANCE'
  | 'FINANCE_ORDERS'
  | 'FINANCE_CREDIT'
  | 'FINANCE_CREDIT_MANAGE'
  | 'FINANCE_CREDIT_BUY'
  | 'CARDS'
  | 'RECARDING'
  | 'CARDS_REQUEST'
  | 'PEOPLE'
  | 'PEOPLE_EMPLOYEES'
  | 'PEOPLE_EMPLOYEES_CREATE'
  | 'PEOPLE_EMPLOYEES_MANAGE'
  | 'PEOPLE_WORKGORUPS'
  | 'PEOPLE_WORKGORUPS_CREATE'
  | 'PEOPLE_WORKGORUPS_MANAGE'
  | 'CORPORATE'
  | 'CORPORATE_LOGIN'
  | 'CORPORATE_LOGIN_USERS'
  | 'CORPORATE_LOGIN_ROLES'
  | 'CORPORATE_LOGIN_HISTORY'
  | 'SUPPORT'
  | 'SERVICES'
  | 'SERVICES_SWILE_MAIS'

const addItemWhenHasFeatureAllowed = <T = Record<string, unknown>>(
  item: T,
  rule: RouteRules
) =>
  process.env.NODE_ENV === 'production'
    ? process.env.NEXT_PUBLIC_FEATURES_ALLOWED?.includes(rule)
      ? item
      : null
    : item

const prepareGroups = (arr: unknown[]) => arr.filter(Boolean)
